import { useEffect } from 'react'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { useAiChat } from '@factorialco/f0-react'
import { Pencil, Delete } from '@factorialco/f0-react/icons/app'
import {
  CompensationPlanDetailRouteFragment,
  IncentivesCompensationPlanPeriodFragment,
  IncentivesCompensationPlanDetailDocument,
  IncentivesCompensationPlanDetailRoutePeriodDocument,
  IncentivesCompensationPlanPayoutsDocument,
} from 'generated/resources/graphql'
import i18n from 'modules/core/lib/i18n'

import { useApolloClient } from '@apollo/client'
import { compact } from 'lodash'

import { useQuery } from 'modules/core/hooks/useQuery'
import { recurrenceLabel } from 'modules/incentives/lib/recurrence'
import { dateBackendToUI } from 'modules/core/lib/dates'
import {
  getCurrentPeriod,
  getFirstNotExecutedPeriod,
  getPeriodStatus,
  isExecutedPeriod,
  PERIOD_STATUS,
} from 'modules/incentives/lib/period'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useNavigation } from 'modules/core/hooks/useNavigation'

interface CompensationPlanDetailHeaderProps {
  plan: CompensationPlanDetailRouteFragment
}

const isPeriodQueuedOrGenerating = (period: IncentivesCompensationPlanPeriodFragment) => {
  const lastExecution = period.lastExecution
  return (
    lastExecution?.generationStatus &&
    ['queued', 'generating'].includes(lastExecution.generationStatus)
  )
}

export const CompensationPlanDetailHeader = ({ plan }: CompensationPlanDetailHeaderProps) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const aiChat = useAiChat()
  const client = useApolloClient()

  const [searchParams] = useSearchParams()
  const periodIdParam = searchParams.get('periodId')

  const periods = plan.compensationPlanPeriods

  const selectedPeriod =
    periods.find((p) => p.id === periodIdParam) ??
    getFirstNotExecutedPeriod(periods) ??
    getCurrentPeriod(periods)

  const isSelectedPeriodClosed =
    selectedPeriod && getPeriodStatus(selectedPeriod) === PERIOD_STATUS.CLOSED

  const periodQueuedOrGenerating = periods.find(isPeriodQueuedOrGenerating)

  const sendToCompensationDisabled = Boolean(
    !selectedPeriod ||
    !isSelectedPeriodClosed ||
    periodQueuedOrGenerating ||
    isExecutedPeriod(selectedPeriod)
  )

  const canUpdatePlan = plan._policies?.update.can ?? false
  const canDeletePlan = plan._policies?.delete.can ?? false
  const canSendToCompensation = selectedPeriod?._policies?.createExecution.can ?? false

  const openOne = (message: string) => {
    if (aiChat.enabled) {
      aiChat.setOpen(true)
      aiChat.sendMessage(message)
    }
  }

  const onSendToCompensation = () => {
    const params = new URLSearchParams(searchParams)
    params.set('sendToCompensation', 'true')
    params.set('periodId', selectedPeriod?.id ?? '')
    params.delete('deletePlan')
    navigate(
      `${navigation.compensations.incentives.plans.id.fullPath({ id: plan.id })}?${params.toString()}`
    )
  }

  const otherActions = compact([
    canUpdatePlan && {
      label: i18n.t('incentives.detail.update_plan.label'),
      icon: Pencil,
      onClick: () =>
        openOne(i18n.t('incentives.detail.update_plan.one_prompt', { planName: plan.name })),
    },
    canDeletePlan && {
      label: i18n.t('incentives.detail.delete_plan.label'),
      icon: Delete,
      critical: true as const,
      onClick: () => {
        const params = new URLSearchParams(searchParams)
        params.set('deletePlan', 'true')
        params.delete('sendToCompensation')
        navigate(
          `${navigation.compensations.incentives.plans.id.fullPath({ id: plan.id })}?${params.toString()}`
        )
      },
    },
  ])

  // TODO: Refactor to use a GraphQL subscription instead of polling
  const { data: periodData } = useQuery(IncentivesCompensationPlanDetailRoutePeriodDocument, {
    variables: { periodId: periodQueuedOrGenerating?.id ?? '' },
    skip: !periodQueuedOrGenerating,
    pollInterval: 5000,
  })

  useEffect(() => {
    const executedPeriod = periodData?.incentives.compensationPlanPeriod
    if (executedPeriod && !isPeriodQueuedOrGenerating(executedPeriod)) {
      client.refetchQueries({
        include: [
          IncentivesCompensationPlanDetailDocument,
          IncentivesCompensationPlanPayoutsDocument,
        ],
      })
    }
  }, [periodData, client])

  return (
    <ResourceHeader
      title={plan.name}
      primaryAction={
        canSendToCompensation
          ? {
              label: i18n.t('incentives.detail.send_to_compensation.label'),
              disabled: sendToCompensationDisabled,
              loading: Boolean(periodQueuedOrGenerating),
              onClick: onSendToCompensation,
            }
          : undefined
      }
      otherActions={otherActions}
      metadata={[
        {
          label: i18n.t('incentives.plan.recurrence'),
          value: {
            content: recurrenceLabel(plan.recurrence),
            type: 'text',
          },
        },
        {
          label: i18n.t('incentives.plan.start_date'),
          value: {
            formattedDate: dateBackendToUI(plan.startsOn),
            type: 'date',
          },
        },
      ]}
    />
  )
}
